'use client';

import { FC, useState } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { FormTextField, FormAutocomplete, FormDatePicker } from '@/components/common/FormComponents';
import { StandardDialog } from '@/components/common/StandardDialog';
import { FormLabel } from '@/components/common/FormLabel';
import { useBackendStore } from '@/stores/backendStore';
import { useSnackbar } from '@/hooks/useSnackbar';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { ProjectDto, CommunicationDto, CreateCommunicationRequest, VisibilityDto } from '@/api/ApiClient';
import { log } from '@/lib/utils/logger';

import { COMMUNICATION_FORMAT_OPTIONS } from '@/lib/types/communicationTypes';
import { nullIfBlank } from '@/lib/utils/nullIfBlank';
import { useUserInteraction } from '@/hooks/useUnsavedChanges';
import { validateRequiredToSave } from '@/lib/utils/saveValidation';
import { createCommunicationSaveValidation } from '@/lib/validation/saveValidationBuilders';
import { getProblemDetail } from '@/lib/utils/apiErrors';

interface AddCommunicationDialogProps {
  open: boolean;
  onClose: () => void;
  project: ProjectDto;
  onSubmit?: (communication: CommunicationDto) => void;
}

const AddCommunicationDialog: FC<AddCommunicationDialogProps> = ({ open, onClose, project, onSubmit }) => {
  const backend = useBackendStore((state) => state.getBackend());
  const { showSnackbar } = useSnackbar();
  const { hasUnsavedChanges, markAsInteracted } = useUserInteraction();

  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [selectedFormat, setSelectedFormat] = useState<string | undefined>(undefined);
  const [title, setTitle] = useState<string>('');
  const [participants, setParticipants] = useState<string>('');
  const [content, setContent] = useState<string>('');

  const [formatError, setFormatError] = useState(false);
  const [titleError, setTitleError] = useState(false);

  const formatOptions = COMMUNICATION_FORMAT_OPTIONS;

  const handleClose = () => {
    setSelectedDate(new Date());
    setSelectedFormat(undefined);
    setTitle('');
    setParticipants('');
    setContent('');
    setFormatError(false);
    setTitleError(false);
    onClose();
  };

  const handleSubmit = async () => {
    const saveValidation = validateRequiredToSave(
      createCommunicationSaveValidation({
        format: nullIfBlank(selectedFormat),
        title,
      })
    );
    setFormatError(!!saveValidation.fieldErrors.format);
    setTitleError(!!saveValidation.fieldErrors.title);

    if (!saveValidation.isValid) {
      showSnackbar('Please fill out all required fields.', 'error');
      return;
    }

    if (!project?.id) {
      showSnackbar('No project selected.', 'error');
      return;
    }

    try {
      const payload: CreateCommunicationRequest = {
        projectID: project.id,
        title: title.trim(),
        format: nullIfBlank(selectedFormat),
        participants: participants || '',
        text: content || '',
        date: selectedDate ?? new Date(),
        visibility: VisibilityDto.OnPublication,
      };

      const savedCommunication = await backend.communication.create(payload);
      log.debug('[AddCommunicationDialog] Saved communication:', savedCommunication);

      onSubmit?.(savedCommunication);
      showSnackbar('Communication created successfully.', 'success');
      handleClose();
    } catch (error: unknown) {
      showSnackbar(getProblemDetail(error) ?? 'Failed to create communication.', 'error');
    }
  };

  if (!project) {
    return (
      <StandardDialog
        open={open}
        onClose={onClose}
        title="Add Communication"
        maxWidth="md"
        onSubmit={() => {}}
        submitText="Add Communication"
        submitDisabled
      >
        <Box sx={{ py: 2 }}>No project selected.</Box>
      </StandardDialog>
    );
  }

  return (
    <StandardDialog
      open={open}
      onClose={handleClose}
      title="Add Communication"
      maxWidth="md"
      onSubmit={handleSubmit}
      submitText="Add Communication"
      hasUnsavedChanges={hasUnsavedChanges}
      unsavedChangesMessage="You have unsaved changes. Are you sure you want to discard them?"
    >
      <Box display="flex" gap={2}>
        <Box flex={1}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <FormDatePicker
              label="Date"
              value={selectedDate}
              onChange={(newValue) => {
                setSelectedDate(newValue);
                markAsInteracted();
              }}
              required
            />
          </LocalizationProvider>
        </Box>
      </Box>

      <Divider />

      <FormAutocomplete
        options={formatOptions}
        getOptionLabel={(option) => option.value}
        value={formatOptions.find((opt) => opt.key === selectedFormat) || null}
        onChange={(_, newValue) => {
          setSelectedFormat(nullIfBlank(newValue?.key));
          markAsInteracted();
        }}
        label="Format"
        required
        error={formatError}
      />

      <FormTextField
        label="Title"
        required
        value={title}
        onChange={(e) => {
          setTitle(e.target.value);
          markAsInteracted();
        }}
        size="small"
        error={titleError}
        fullWidth
      />

      <FormTextField
        label="Participants"
        value={participants}
        onChange={(e) => {
          setParticipants(e.target.value);
          markAsInteracted();
        }}
        size="small"
        fullWidth
        placeholder="e.g., John Doe, Jane Smith"
      />

      <Divider />

      <Box width="100%">
        <Typography variant="subtitle2" gutterBottom>
          <FormLabel optional>Content</FormLabel>
        </Typography>
        <Box width="100%">
          <RichTextEditor
            value={content}
            onChange={(value) => {
              setContent(value);
              markAsInteracted();
            }}
          />
        </Box>
      </Box>
    </StandardDialog>
  );
};

export { AddCommunicationDialog };
